// import { Close } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import GetAppIcon from '@mui/icons-material/GetApp';
// import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton, Link, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGrid } from '@mui/x-data-grid';
// import { duration } from '@mui/x-data-grid';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import xmlFormat from 'xml-formatter';

import { DataAPIContext, GET_ONE } from '../api';
import LogViewer from '../components/LogViewer';
// import PageMeta from '../components/PageMeta';
// import { a11yProps } from '../components/RecordDetails';
import TabPanel, { a11yProps } from '../components/TabPanel';
import { ContextStore } from '../store/contextStore';
import { copyTextToClipboard, useIsWidthUp } from '../utils';
import ImportReimportDialog from './ImportReimportDialog';
import { getImportStateDisplay } from './utils';

const useStyles = makeStyles((theme: any) => {
    return {
        root: {
            border: '1px solid #d8d8d8',
            borderRadius: '5px',
            height: 'calc(100svh - 7rem)',
            backgroundColor: theme.palette.background.paper,
            zIndex: 10,
            [theme.breakpoints.down('sm')]: {
                height: 'calc(100svh - 11rem)',
                marginTop: '2rem',
            },
        },
        scrollableRoot: {
            height: 'calc(100svh - 9rem)',
            overflowY: 'auto',
            [theme.breakpoints.down('sm')]: {
                height: 'calc(100svh - 13rem)',
            },
        },
        modalContent: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            height: '80%',
            overflowY: 'scroll',
            backgroundColor: theme.palette.background.paper,
            border: '1px solid #bbb',
            borderRadius: '5px',
            // boxShadow: theme.shadows[5],
            padding: theme.spacing(2, 4, 3),
        },
    };
});

// The preview dialog sits at z-index 999999, above MUI's default tooltip layer.
const tooltipPopperProps = { style: { zIndex: 1000000 } };

const ImportRunInfo = (props: any) => {
    const run = props.run;
    const { copyToClipboard } = props;

    const onRunClick = (ev) => {
        ev.preventDefault();
        props.onRunClick && props.onRunClick(run.id, ev);
        return false;
    }

    const onGroupIDClick = (ev) => {
        ev.preventDefault();
        console.log('group click', run.group);
        props.onRunClick && props.onRunClick(run.group, ev);
        return false;
    }

    return (<>
        <Typography>
            <strong>{ (props.idx !== null && !isNaN(props.idx)) ? `${props.idx + 1}. ` : null }ID:</strong>{' '}
            { props.is_group ? <Link href={`/import/run/list/${run.id}`} onClick={onRunClick}>{run.id}</Link> : <>{run.id}</> }{' '}
            <Tooltip title="Copy import ID" PopperProps={tooltipPopperProps}>
                <IconButton size="small" onClick={() => copyToClipboard(run.id, `Import ID`)}>
                    <FileCopyOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Typography>

        { (run.group && !props.is_group) && <>
            <Typography>
                <strong>Import group ID:</strong>{' '}
                <Link href={`/import/run/list/${run.group}`} onClick={onGroupIDClick}>{run.group}</Link>
                <Tooltip title="Copy group ID" PopperProps={tooltipPopperProps}>
                    <IconButton size="small" onClick={() => copyToClipboard(run.group, `Import group ID`)}>
                        <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Typography>
        </> }

        {run.request_id && (
            <>
                <Typography>
                    <strong>Request ID:</strong> {run.request_id}{' '}
                    <Tooltip title="Copy request ID" PopperProps={tooltipPopperProps}>
                        <IconButton size="small" onClick={() => copyToClipboard(run.request_id, 'Request ID')}>
                            <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Typography>
            </>
        )}

        {run.input_options && run.input_options.url ? (
            <>
                <Typography>
                    <strong>Fetched at:</strong> {moment.tz(run.created_ts, 'UTC').format('MMM DD, YYYY, h:mm:ss A z')}
                </Typography>
                <Typography>
                    <strong>Fetched from:</strong>{' '}
                    <Link href={run.input_options.url} target="_blank">
                        {run.input_options.url}
                    </Link>
                    <Tooltip title="Copy URL" PopperProps={tooltipPopperProps}>
                        <IconButton size="small" onClick={() => copyToClipboard(run.input_options.url, 'URL')}>
                            <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {run.input_options.get_status ? <>({run.input_options.head_status ? `${run.input_options.head_status}/${run.input_options.get_status}` : `${run.input_options.get_status}`})</> : null}
                </Typography>
            </>
        ) : (
            <>
                <Typography>
                    <strong>Uploaded at:</strong> {moment.tz(run.created_ts, 'UTC').format('MMM DD, YYYY, h:mm:ss A z')}
                </Typography>
            </>
        )}
        <Typography>
            <strong>Processing started at:</strong> {moment.tz(run.processing_started_ts, 'UTC').format('MMM DD, YYYY, h:mm:ss A z')}
        </Typography>
        {run.processing_finished_ts && (
            <Typography>
                <strong>Processing finished:</strong> {moment.tz(run.processing_finished_ts, 'UTC').format('MMM DD, YYYY, h:mm:ss A z')}
            </Typography>
        )}


        <Typography>
            <strong>Status:</strong> {getImportStateDisplay(run.state)}
        </Typography>
    </>);
}

const ImportPreview = (props: any) => {
    const classes = useStyles();
    const theme = useTheme();
    const { hasPerm } = React.useContext(ContextStore);

    const is_group = props.data?.imports?.length > 0;
    const [previewData, setPreviewData] = React.useState(null);
    const [performanceData, setPerformanceData] = React.useState(null);
    const [errorLog, setErrorLog] = React.useState(null);
    const [currentTab, setCurrentTab] = React.useState(props.lastTabSelected || 0);
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm')
    const [isXMLDataModified, setIsXMLDataModified]   = React.useState(false);
    const dataAPI = React.useContext(DataAPIContext);
    const importData = props.data;

    const handleTabChange = (ev, newTab) => {
        setCurrentTab(newTab);
    };

    const tabPanelProps = (index, h) => {
        return {
            className: classes.tabPanel,
            value: currentTab,
            index: index,
        };
    };

    const downloadImportFile = (filename: string, text: string) => {
        dataAPI(GET_ONE, 'import_run', { id: importData.id, urlSuffix: 'input_data/' }).then((res) => {
            if (res.statusCode === 200) {
                let element = document.createElement('a');
                element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(res.data.input_data));
                element.setAttribute('download', filename);

                element.style.display = 'none';
                document.body.appendChild(element);

                element.click();

                document.body.removeChild(element);
            } else {
                // enqueueSnackbar with time duration

                enqueueSnackbar('Error fetching input data', { variant: 'error' });
            }
        });
    };

    const onCopyXML = (ev) => {
        if (!!previewData) {
            copyToClipboard(previewData, 'XML');
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 750 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 750 });
        }
    };

    React.useEffect(() => {
        if (currentTab !== 1 || !!previewData) {
            return;
        }
        let active = true;
        dataAPI(GET_ONE, 'import_run', { id: importData.id, urlSuffix: 'input_data/' }).then((res) => {
            if (!active) {
                return;
            }
            if (res.statusCode === 200) {
                setPreviewData(res.data.input_data);
            } else {
                enqueueSnackbar(res.data?.message ?? 'Error fetching input data', { variant: 'error' });
            }
        });
        return () => {
            active = false;
        };
    }, [dataAPI, currentTab, previewData]);

    React.useEffect(() => {
        if (currentTab !== 2 || !!errorLog) {
            return;
        }
        let active = true;
        dataAPI(GET_ONE, 'import_run', { id: importData.id, urlSuffix: 'error_log/' }).then((res) => {
            if (!active) {
                return;
            }
            if (res.statusCode === 200) {
                setErrorLog(res.data.error_log);
            } else {
                enqueueSnackbar(res.data?.message ?? "Error fetching error log", { variant: 'error' });
            }
        });
        return () => {
            active = false;
        };
    }, [dataAPI, currentTab, errorLog]);

    React.useEffect(() => {
        if (currentTab !== 3 || !!performanceData) {
            return;
        }
        let active = true;
        dataAPI(GET_ONE, 'import_run', { id: importData.id, urlSuffix: 'stats/' }).then((res) => {
            if (!active) {
                return;
            }
            if (res.statusCode === 200) {
                setPerformanceData(res.data.stats);
            } else {
                enqueueSnackbar(res.data?.message ?? 'Error fetching stats', { variant: 'error' });
            }
        });
        return () => {
            active = false;
        };
    }, [dataAPI, currentTab, errorLog]);

    const onDialogClose = (ev) => {
        if(!isXMLDataModified){
            props.onClose && props.onClose(ev);
            return;
        }
        if(window.confirm('Are you sure you want to leave without saving changes?')){
            props.onClose && props.onClose(ev);
        }
    };

    const timeFormatter = (p: any) => {
        const f = parseFloat(p.value);
        if (Math.abs(f) > 1e99) {
            return f < 0 ? '-Infinity' : 'Infinity';
        } else {
            return f?.toFixed(3);
        }
    };

    return (
        <Dialog open={!!props.id} maxWidth="xl" fullWidth onClose={onDialogClose} sx={{ zIndex: 999999 }}>
            <DialogTitle>
                <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={0}>
                    <Grid item xs={10} container direction="row" alignItems="center" spacing={0}>
                    <Grid item xs={11} sm="auto">
                       <Typography fontWeight={500} fontSize={18}>
                            Import {importData.id}{' '}
                            <Tooltip title="Copy import ID" PopperProps={tooltipPopperProps}>
                                <IconButton size="small" onClick={() => copyToClipboard(importData.id, 'Import ID')}>
                                    <FileCopyOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            { importData.group && <>
                                of group {importData.group}
                                <Tooltip title="Copy group ID" PopperProps={tooltipPopperProps}>
                                    <IconButton size="small" onClick={() => copyToClipboard(importData.group, 'Import group ID')}>
                                        <FileCopyOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </> }
                        </Typography>
                    </Grid>
                    <Grid item xs={1}>
                    </Grid>
                    </Grid>
                    <Grid item xs={2} container justifyContent="flex-end">
                        <Tooltip title="Close" PopperProps={tooltipPopperProps}>
                            <IconButton onClick={onDialogClose} aria-label="close" size="small" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent>
                { !is_group && <>
                    <Tabs value={currentTab} variant='scrollable' scrollButtons="auto"  onChange={handleTabChange}  indicatorColor="primary" textColor="primary">
                        <Tab label="Info" {...a11yProps(0)} />
                        <Tab label="Input data" {...a11yProps(1)} />
                        { hasPerm('import', 'internals') && <Tab label="Error log" {...a11yProps(2)} /> }
                        { hasPerm('import', 'internals') && <Tab label="Performance" {...a11yProps(3)} /> }
                        { hasPerm('import', 'reimport') && <Tab label="Reimport" {...a11yProps(4)} /> }
                    </Tabs>
                </> }
                <TabPanel {...tabPanelProps(0)}>
                    <Box mt={2} mb={2}>
                        <Grid container direction="row" spacing={2}>

                        { is_group && <>
                            <Grid item xs={12}>
                                <Typography>
                                    <strong>Import group ID:</strong> {importData.id}{' '}
                                    <Tooltip title="Copy group ID" PopperProps={tooltipPopperProps}>
                                        <IconButton size="small" onClick={() => copyToClipboard(importData.id, `Import ID`)}>
                                            <FileCopyOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <br/>
                                    <strong>Group size:</strong> {importData.imports.length}
                                </Typography>
                            </Grid>
                        </> }

                        { is_group ? importData.imports.map((run, idx) => (<Grid item xs={12}>
                            <ImportRunInfo key={idx} idx={idx} run={run} is_group={is_group} onRunClick={props.onRunClick} copyToClipboard={copyToClipboard}/>
                        </Grid>)) : <Grid item xs={12}><ImportRunInfo run={importData} is_group={is_group} onRunClick={props.onRunClick} copyToClipboard={copyToClipboard}/></Grid> }

                        {/* {importData.sources?.length > 0 ? (
                            <div>
                                <Typography>
                                    <strong>Status:</strong> File was accepted
                                </Typography>
                                {importData.sources.length === 1 ? (
                                    <ImportPreviewSource source={importData.sources[0]} idx={0} cnt={1} />
                                ) : (
                                    <>
                                        <Typography>
                                            <strong>Sources:</strong>
                                        </Typography>
                                        {importData.sources.map((source: any, idx: number) => (
                                            <ImportPreviewSource source={source} idx={idx} cnt={importData.sources.length} />
                                        ))}
                                    </>
                                )}
                            </div>
                        ) : importData.processing_started_ts ? (
                            importData.processing_finished_ts ? (
                                <Typography color="secondary">
                                    <strong>Status:</strong> File or all of its sources were rejected. You may reach out to <Link href="mailto:Operations@CuratedPlanet.com">Operations@CuratedPlanet.com</Link> or your technical contact for additional details.
                                </Typography>
                            ) : (
                                <Typography>
                                    <strong>Status:</strong> Starting to process the file
                                </Typography>
                            )
                        ) : (
                            <Typography>Queued</Typography>
                        )} */}
                        </Grid>
                        <Grid item xs={12} container direction="row" spacing={2}>
                            <Grid item xs={6}></Grid>
                        </Grid>
                    </Box>
                </TabPanel>
                { !is_group && <>
                    <TabPanel {...tabPanelProps(1)}>
                        <Grid container direction="row" spacing={2}>
                            <Grid item xs={12}>
                                {previewData === null ? (
                                    <CircularProgress />
                                ) : (
                                    <Box sx={{ maxHeight: '400px', overflowY: 'scroll' }}>
                                        <Typography>
                                            <pre>
                                                {xmlFormat(previewData, {
                                                    collapseContent: true,
                                                })}
                                            </pre>
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                            {!!previewData && (
                                <>
                                    <Grid item xs={6}>
                                        <Button variant="outlined" color="primary" onClick={() => downloadImportFile(`${importData.id}-input.xml`, importData.input_data)}>
                                            <GetAppIcon />
                                            &nbsp;Download
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6} container justifyContent="flex-end">
                                        <Tooltip title={isSmallScreen ? 'Copy to clipboard' : ''} PopperProps={tooltipPopperProps}>
                                            <Button variant="outlined" color="primary" onClick={onCopyXML}>
                                                <FileCopyOutlinedIcon style={{marginRight:'0.25rem'}}/>
                                            {!isSmallScreen &&  `Copy to clipboard`}
                                            </Button>
                                        </Tooltip>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </TabPanel>
                    <TabPanel {...tabPanelProps(2)}>
                        <Grid container direction="row" spacing={2}>
                            <Grid item xs={12}>
                                {errorLog === null && (
                                        <CircularProgress />
                                )}
                                {errorLog !== null && (
                                        <LogViewer logs={errorLog} height={400} filenamePrefix={importData.id} />
                                )}
                            </Grid>
                        </Grid>
                    </TabPanel>
                    <TabPanel {...tabPanelProps(3)}>
                        <Grid container direction="row" spacing={2}>
                            <Grid item xs={12}>
                                {performanceData === null ? (
                                    <CircularProgress />
                                ) : (
                                    <Box sx={{ height: 400, width: '100%', mt: theme.spacing(2) }}>
                                        <DataGrid
                                            autoHeight={false}
                                            checkboxSelection={false}
                                            density="compact"
                                            rows={performanceData}
                                            getRowId={(row) => row.key}
                                            columns={[
                                                { field: 'key', headerName: 'Label', flex: 2 },
                                                { field: 'n', headerName: 'Count', flex: 1 },
                                                { field: 'average', headerName: 'Average time (s)', flex: 1, valueFormatter: timeFormatter },
                                                { field: 'min', headerName: 'Min time (s)', flex: 1, valueFormatter: timeFormatter },
                                                { field: 'max', headerName: 'Max time (s)', flex: 1, valueFormatter: timeFormatter },
                                                { field: 'v', headerName: 'Total time (s)', flex: 1, valueFormatter: timeFormatter },
                                            ]}
                                        />
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </TabPanel>
                    <TabPanel {...tabPanelProps(4)}>
                        <ImportReimportDialog setIsXMLDataModified={setIsXMLDataModified}  id={importData.id} data={importData} onResubmited={props.onImportReimported} setIs />
                    </TabPanel>
                </> }
            </DialogContent>
        </Dialog>
    );
};

export default ImportPreview;
